import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Toaster, toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Plus, BookOpen, X, Paperclip } from 'lucide-react';
import { z } from 'zod';
import { useZodForm, FormField, FormItem, FormLabel, FormMessage, FormDescription } from './components/ui/form';
import { FormProvider } from 'react-hook-form';
import { Button } from './components/ui/button';
import { Card } from './components/ui/card';
import { Skeleton } from './components/ui/skeleton';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Badge } from './components/ui/badge';
import { cn } from './lib/utils';
import { Layout } from './components/layout/layout';
import { Sidebar } from './components/layout/sidebar';
import { AnalyticsDashboard } from './components/analytics/AnalyticsDashboard';
import { ReviewModerationPanel } from './components/admin/ReviewModerationPanel';
import { CategoryManagement } from './components/categories';
import { PromptCard as NewPromptCard } from './components/prompt/PromptCard';
import { UsePromptHandler } from './components/prompt/UsePromptHandler';
import PromptDetailView from './components/prompt/PromptDetailView';
import { FilterBar } from './components/filters/FilterBar';
import { SearchFacetsPanel } from './components/search/SearchFacetsPanel';
import { SearchResultsSummary } from './components/search/SearchResultsSummary';
import { AuthGuard, LoginScreen } from './components/auth';
import { api, Prompt, Category, Tag, PagedResult, SavedSearch, PromptQualityAnalysis } from './services/api';
import { AuthSessionExpiredError } from './services/authService';
import { loginRequest } from './config/authConfig';
import { AdvancedSearchCriteria, SearchResult } from './services/api';
import { QualityReviewModal } from './components/prompt/QualityReviewModal';
import { useAutoApproveThreshold } from './components/prompt/NeedsImprovementBadge';
import { appConfig } from './config/appConfig';
import type { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
// MSAL hook - create a safe wrapper that won't break when auth is disabled
const useSafeMsal = (): { instance: IPublicClientApplication | null; accounts: AccountInfo[] } => {
  if (!appConfig.enableAuthentication) {
    // Return empty/safe defaults when authentication is disabled
    return {
      instance: null,
      accounts: [] as AccountInfo[]
    };
  }
  
  // Use actual MSAL when authentication is enabled
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { instance, accounts } = useMsal();
  return { instance, accounts };
};

// Validate configuration on startup
const validateConfig = (): string[] => {
  const issues: string[] = [];
  
  if (!appConfig.apiUrl) {
    issues.push('API URL is not configured');
  }
  
  if (appConfig.enableAuthentication) {
    if (!appConfig.authClientId) {
      issues.push('Authentication is enabled but Client ID is not configured');
    }
    if (!appConfig.authTenantId) {
      issues.push('Authentication is enabled but Tenant ID is not configured');
    }
  }
  
  return issues;
};

// Log configuration on startup
console.log('App Configuration:', {
  ...appConfig,
  authClientId: appConfig.authClientId ? '***' : undefined,
  authTenantId: appConfig.authTenantId ? '***' : undefined
});

// Validate configuration
const configIssues = validateConfig();
if (configIssues.length > 0) {
  console.warn('Configuration issues detected:', configIssues);
  configIssues.forEach(issue => console.warn(`- ${issue}`));
}

// Form submission shape (mirrors zod schema in SubmitPromptView)
interface NewPromptInput {
  title: string;
  promptText: string;
  description?: string | '';
  submittedBy: string;
  categoryId: number;
  tags: string[];
}

// Props interfaces for components
interface BrowsePromptsViewProps {
  prompts: PagedResult | null;
  categories: Category[];
  availableTags: Tag[];
  loading: boolean;
  error: string | null;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectedCategory: number | undefined;
  setSelectedCategory: (categoryId: number | undefined) => void;
  selectedTags: string[];
  handleTagToggle: (tagName: string) => void;
  clearFilters: () => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  sort: string;
  setSort: (value: string) => void;
  onMetricsChange: (promptId: number, newMetrics: Partial<{ rating: number; uses: number; likes: number; comments: number }>) => void;
  // Advanced search support
  advancedSearchCriteria?: AdvancedSearchCriteria;
  onAdvancedSearch: (criteria: AdvancedSearchCriteria) => void;
  searchResults?: SearchResult;
  recentSearches: string[];
  onRecentSearchClick: (search: string) => void;
  onClearRecentSearches: () => void;
  // Interactive detail view support
  isAuthenticated: boolean;
  userId: string;
  onPromptDoubleClick: (promptId: number) => void;
  onSaveSearch?: (name: string, description: string, criteria: AdvancedSearchCriteria) => void;
}

interface SubmitPromptViewProps {
  categories: Category[];
  currentUserDisplayName: string;
  onSuccess: () => void;
  addOptimisticPrompt: (values: NewPromptInput) => number;
  finalizeOptimisticPrompt: (tempId: number, real: Prompt) => void;
  revertOptimisticPrompt: (tempId: number) => void;
  availableTags: Tag[];
}

// Main App Component matching Figma design
export default function AIPromptLibrary() {
  // Use safe MSAL hook that handles authentication being disabled
  const { instance, accounts } = useSafeMsal();
  
  // Check if user is authenticated
  const isAuthenticated = accounts.length > 0;
  
  // Get current user info for forms
  const currentAccount: AccountInfo | null = accounts.length > 0 ? accounts[0] : null;
  const currentUserDisplayName = currentAccount?.name || currentAccount?.username || 'User';
  const idClaims = currentAccount?.idTokenClaims as { [key: string]: unknown } | undefined;
  const userRoles = Array.isArray(idClaims?.roles) ? idClaims!.roles as string[] : [];
  const isAdmin = userRoles.includes('Admin');
  
  // Initialize API service with MSAL instance and account
  useEffect(() => {
    if (appConfig.enableAuthentication) {
      const currentAccount: AccountInfo | null = accounts.length > 0 ? accounts[0] : null;
      api.initialize(instance, currentAccount);
    } else {
      api.initialize(null, null);
    }
  }, [instance, accounts]);
  
  const [prompts, setPrompts] = useState<PagedResult | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentView, setCurrentView] = useState<'browse' | 'submit' | 'analytics' | 'categories' | 'myPrompts' | 'moderation' | 'savedSearches'>('browse');
  
  // Detail view state — initialized from URL (e.g. direct navigation to /prompts/2)
  const [selectedPromptId, setSelectedPromptId] = useState<number | null>(() => {
    const match = window.location.pathname.match(/^\/prompts\/(\d+)$/);
    return match ? parseInt(match[1], 10) : null;
  });

  // Sync browser back/forward navigation with detail view state
  useEffect(() => {
    const onPopState = () => {
      const match = window.location.pathname.match(/^\/prompts\/(\d+)$/);
      setSelectedPromptId(match ? parseInt(match[1], 10) : null);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // Mock user for demo - in real app this would come from authentication
  const mockUserId = 'demo-user-123';
  
  // Search/Filter state
  const [searchTerm, setSearchTerm] = useState('');
  // Debounced copy of searchTerm — the search effect keys off this so typing
  // fires at most one request per pause, never one per keystroke (FR-8.5/BUG-070)
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  // Monotonic id so late responses from superseded searches are ignored (FR-8.5)
  const searchRequestSeq = useRef(0);
  const [selectedCategory, setSelectedCategory] = useState<number | undefined>();
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(() => {
    const p = parseInt(new URLSearchParams(window.location.search).get('page') || '1', 10);
    return isNaN(p) || p < 1 ? 1 : p;
  });
  // Tracks whether filter-reset effect has already run once (skips initial mount to preserve URL ?page=N)
  const filterResetMounted = useRef(false);
  const [sort, setSort] = useState<string>('createdAt_desc');
  
  // Advanced search state
  const [advancedSearchCriteria, setAdvancedSearchCriteria] = useState<AdvancedSearchCriteria | undefined>();
  const [searchResults, setSearchResults] = useState<SearchResult | undefined>();
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [isUsingAdvancedSearch, setIsUsingAdvancedSearch] = useState(false);
  
  // Handler functions for interactive detail view
  const handlePromptDoubleClick = (promptId: number) => {
    window.history.pushState({ promptId }, '', `/prompts/${promptId}`);
    setSelectedPromptId(promptId);
  };

  const handleBackFromDetail = () => {
    setSelectedPromptId(null);
    const qs = currentPage > 1 ? `?page=${currentPage}` : '';
    window.history.replaceState(null, '', `/${qs}`);
  };

  const handleDeletePrompt = (promptId: number) => {
    setSelectedPromptId(null);
    window.history.replaceState(null, '', '/');
    setPrompts(prev => {
      if (!prev) return prev;
      const items = prev.items.filter(p => p.id !== promptId);
      const totalCount = Math.max(0, prev.totalCount - 1);
      return {
        ...prev,
        items,
        totalCount,
        totalPages: Math.max(1, Math.ceil(totalCount / prev.pageSize))
      };
    });
  };
  
  // Function to handle metrics changes for individual prompts
  const handlePromptMetricsChange = useCallback((promptId: number, newMetrics: Partial<{ rating: number; uses: number; likes: number; comments: number }>) => {
    setPrompts(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        items: prev.items.map((prompt: Prompt) => 
          prompt.id === promptId 
            ? { ...prompt, ...newMetrics }
            : prompt
        )
      };
    });
  }, []);
  
  // Debounce raw keystrokes into debouncedSearchTerm (FR-8.5/BUG-070)
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  // Search prompts based on current filters/pagination when in browse view
  const searchPrompts = useCallback(async () => {
    if (currentView !== 'browse') return;
    const requestId = ++searchRequestSeq.current;
    setLoading(true);
    try {
      setError(null);

      let result: PagedResult;

      if (isUsingAdvancedSearch && advancedSearchCriteria) {
        // Use advanced search
        const advancedResult = await api.advancedSearchPrompts(advancedSearchCriteria);
        if (requestId !== searchRequestSeq.current) return; // superseded
        setSearchResults(advancedResult);
        result = advancedResult; // SearchResult extends PagedResult
      } else {
        // Use basic search
        result = await api.searchPrompts({
          searchTerm: debouncedSearchTerm || undefined,
          categoryId: selectedCategory,
          tags: selectedTags.length > 0 ? selectedTags : undefined,
          page: currentPage,
          pageSize: 20,
          sort
        });
        if (requestId !== searchRequestSeq.current) return; // superseded
      }

      setPrompts(result);

      // Track search terms for recent searches. The dedupe guard lives inside the
      // functional updater so recentSearches is NOT a dependency of this callback —
      // depending on it while also setting it caused the FR-8.5 infinite re-fetch loop.
      if (debouncedSearchTerm) {
        setRecentSearches(prev => prev.includes(debouncedSearchTerm)
          ? prev
          : [debouncedSearchTerm, ...prev.slice(0, 4)]);
      }
    } catch (err) {
      if (requestId !== searchRequestSeq.current) return; // superseded
      console.error('Failed to search prompts:', err);
      setError('Failed to search prompts. Please try again.');
    } finally {
      if (requestId === searchRequestSeq.current) {
        setLoading(false);
      }
    }
  }, [debouncedSearchTerm, selectedCategory, selectedTags, currentPage, currentView, sort, isUsingAdvancedSearch, advancedSearchCriteria]);

  // Load initial data (health, categories, tags) exactly once on mount. This must NOT
  // depend on searchPrompts — that coupling re-ran the whole initial load on every
  // search-state change (FR-8.5/BUG-070). The search effect below owns the first search.
  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Loading initial data...');
      try {
        await api.healthCheck();
        console.log('API health check passed');
      } catch (healthError) {
        console.warn('API health check failed:', healthError);
        throw new Error(`API is not available: ${healthError}`);
      }
      const [categoriesData, tagsData] = await Promise.all([
        api.getCategories(),
        api.getTags()
      ]);
      setCategories(categoriesData);
      setAvailableTags(tagsData);
      console.log('Initial data loaded successfully');
    } catch (err) {
      console.error('Failed to load initial data:', err);
      setError(`Failed to load initial data: ${err}`);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Reset page to 1 when core filters change, but NOT on initial mount (preserves ?page=N URL param)
  useEffect(() => {
    if (!filterResetMounted.current) {
      filterResetMounted.current = true;
      return;
    }
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, selectedTags, sort]);

  // Sync ?page=N URL param with current page state
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (currentPage <= 1) {
      params.delete('page');
    } else {
      params.set('page', String(currentPage));
    }
    const qs = params.toString();
    window.history.replaceState(null, '', qs ? `?${qs}` : window.location.pathname);
  }, [currentPage]);

  // Execute search when any search dependency changes (includes page + filters + view)
  useEffect(() => {
    searchPrompts();
  }, [searchPrompts]);

  const handleTagToggle = (tagName: string) => {
    setSelectedTags(prev => prev.includes(tagName)
      ? prev.filter(t => t !== tagName)
      : [...prev, tagName]);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory(undefined);
    setSelectedTags([]);
    setCurrentPage(1);
    setIsUsingAdvancedSearch(false);
    setAdvancedSearchCriteria(undefined);
    setSearchResults(undefined);
  };

  // Advanced search handlers
  const handleAdvancedSearch = useCallback((criteria: AdvancedSearchCriteria) => {
    setAdvancedSearchCriteria(criteria);
    setIsUsingAdvancedSearch(true);
    setCurrentPage(1);
    
    // Sync basic filters from advanced search
    if (criteria.searchTerm) {
      setSearchTerm(criteria.searchTerm);
    }
    if (criteria.categoryIds?.length === 1) {
      setSelectedCategory(criteria.categoryIds[0]);
    }
    if (criteria.tags?.length) {
      setSelectedTags(criteria.tags);
    }
  }, []);

  const handleRecentSearchClick = useCallback((search: string) => {
    setSearchTerm(search);
    setIsUsingAdvancedSearch(false);
    setAdvancedSearchCriteria(undefined);
    setCurrentPage(1);
  }, []);

  const handleClearRecentSearches = useCallback(() => {
    setRecentSearches([]);
  }, []);

  const handleSaveSearch = useCallback(async (name: string, description: string, criteria: AdvancedSearchCriteria) => {
    try {
      await api.saveSearch({ id: 0, name, description, searchCriteria: criteria, isPublic: false, useCount: 0, createdAt: new Date().toISOString(), lastUsedAt: new Date().toISOString() } as SavedSearch);
      toast.success(`Search "${name}" saved.`);
    } catch {
      toast.error('Failed to save search. Please try again.');
    }
  }, []);

  const handleBasicSearchTermChange = useCallback((term: string) => {
    setSearchTerm(term);
    setIsUsingAdvancedSearch(false);
    setAdvancedSearchCriteria(undefined);
  }, []);

  // Optimistic UI helpers
  const addOptimisticPrompt = (values: NewPromptInput): number => {
    const category = categories.find(c => c.id === values.categoryId) || {
      id: values.categoryId,
      name: 'Unknown',
      color: '#888',
      createdAt: new Date().toISOString(),
      description: undefined
    } as Category;
    const now = new Date().toISOString();
    const tempId = -Date.now(); // negative ID to avoid collision
    const optimisticPrompt: Prompt = {
      id: tempId,
      title: values.title,
      promptText: values.promptText,
      description: values.description || '',
      submittedBy: values.submittedBy,
      createdAt: now,
      updatedAt: now,
      categoryId: values.categoryId,
      category,
      tags: values.tags.map((t, i) => ({ id: tempId - (i + 1), name: t, createdAt: now })),
      isOptimistic: true
    };
    setPrompts(prev => {
      if (!prev) {
        return {
          items: [optimisticPrompt],
            totalCount: 1,
            page: 1,
            pageSize: 20,
            totalPages: 1,
            hasNextPage: false,
            hasPreviousPage: false
        } as PagedResult;
      }
      const items = [optimisticPrompt, ...prev.items];
      const totalCount = prev.totalCount + 1;
      return {
        ...prev,
        items,
        totalCount,
        totalPages: Math.max(1, Math.ceil(totalCount / prev.pageSize))
      };
    });
    return tempId;
  };

  const finalizeOptimisticPrompt = (tempId: number, real: Prompt) => {
    setPrompts(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        items: prev.items.map(p => (p.id === tempId ? { ...real } : p))
      };
    });
  };

  const revertOptimisticPrompt = (tempId: number) => {
    setPrompts(prev => {
      if (!prev) return prev;
      const items = prev.items.filter(p => p.id !== tempId);
      const totalCount = Math.max(0, prev.totalCount - 1);
      return {
        ...prev,
        items,
        totalCount,
        totalPages: Math.max(1, Math.ceil(totalCount / prev.pageSize))
      };
    });
  };

  if (appConfig.enableAuthentication && !isAuthenticated) {
    return <LoginScreen />;
  }

  return (
    <Layout sidebar={<Sidebar currentView={currentView} setCurrentView={setCurrentView} onNavigationClick={() => { setSelectedPromptId(null); window.history.replaceState(null, '', '/'); }} />}>
      <Toaster richColors position="top-right" />
      {selectedPromptId ? (
        <PromptDetailView
          promptId={selectedPromptId}
          onBack={handleBackFromDetail}
          onDelete={() => handleDeletePrompt(selectedPromptId)}
          userId={currentAccount?.localAccountId}
        />
      ) : (
        <>
          {currentView === 'browse' && (
        <BrowsePromptsView
          prompts={prompts}
          categories={categories}
          availableTags={availableTags}
          loading={loading}
          error={error}
          searchTerm={searchTerm}
          setSearchTerm={handleBasicSearchTermChange}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          selectedTags={selectedTags}
          handleTagToggle={handleTagToggle}
          clearFilters={clearFilters}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          sort={sort}
          setSort={setSort}
          onMetricsChange={handlePromptMetricsChange}
          advancedSearchCriteria={advancedSearchCriteria}
          onAdvancedSearch={handleAdvancedSearch}
          searchResults={searchResults}
          recentSearches={recentSearches}
          onRecentSearchClick={handleRecentSearchClick}
          onClearRecentSearches={handleClearRecentSearches}
          isAuthenticated={isAuthenticated}
          userId={mockUserId}
          onPromptDoubleClick={handlePromptDoubleClick}
          onSaveSearch={isAuthenticated ? handleSaveSearch : undefined}
        />
      )}
      {currentView === 'submit' && (
        <AuthGuard message="You need to sign in to submit new prompts to the library.">
          <SubmitPromptView
            categories={categories}
            availableTags={availableTags}
            currentUserDisplayName={currentUserDisplayName}
            addOptimisticPrompt={addOptimisticPrompt}
            finalizeOptimisticPrompt={finalizeOptimisticPrompt}
            revertOptimisticPrompt={revertOptimisticPrompt}
            onSuccess={() => {
              // Already optimistically inserted; just navigate back without refetching
              setCurrentView('browse');
            }}
          />
        </AuthGuard>
          )}
          {currentView === 'categories' && <CategoryManagement isAdmin={isAdmin} />}
          {currentView === 'analytics' && <AnalyticsView onPromptClick={handlePromptDoubleClick} isAdmin={isAdmin} />}
          {currentView === 'myPrompts' && (
            <MyPromptsView
              userId={currentAccount?.localAccountId || mockUserId}
              onPromptClick={handlePromptDoubleClick}
            />
          )}
          {currentView === 'moderation' && (
            <AuthGuard message="You need to sign in to access review moderation.">
              <ReviewModerationPanel />
            </AuthGuard>
          )}
          {currentView === 'savedSearches' && (
            <AuthGuard message="You need to sign in to view your saved searches.">
              <SavedSearchesView onRunSearch={(criteria) => {
                handleAdvancedSearch(criteria);
                setCurrentView('browse');
              }} />
            </AuthGuard>
          )}
        </>
      )}
    </Layout>
  );
}

function AnalyticsView({ onPromptClick, isAdmin = false }: { onPromptClick?: (id: number) => void; isAdmin?: boolean }) {
  return (
    <div className="flex-1 flex flex-col overflow-auto">
      <div className="bg-slate-600 text-white p-6">
        <div className="flex items-center space-x-2 mb-2">
          <BookOpen className="w-6 h-6" />
          <h1 className="text-2xl font-semibold">Analytics Dashboard</h1>
        </div>
        <p className="text-slate-300">Comprehensive insights into prompt library performance</p>
      </div>
      <div className="p-6">
        <AnalyticsDashboard onPromptClick={onPromptClick} isAdmin={isAdmin} />
      </div>
    </div>
  );
}

// My Prompts View Component — shows only the current user's submitted prompts
function MyPromptsView({ userId, onPromptClick }: { userId: string; onPromptClick: (id: number) => void }) {
  const [prompts, setPrompts] = useState<PagedResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.searchPrompts({ submittedByUserId: userId, page: currentPage, pageSize: 10, sort: 'createdAt_desc' })
      .then(data => { if (!cancelled) setPrompts(data); })
      .catch(() => { if (!cancelled) setError('Failed to load your prompts.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId, currentPage]);

  return (
    <div className="flex-1 flex flex-col overflow-auto">
      <div className="bg-slate-700 text-white p-6">
        <div className="flex items-center space-x-2 mb-2">
          <BookOpen className="w-6 h-6" />
          <h1 className="text-2xl font-semibold">My Prompts</h1>
        </div>
        <p className="text-slate-300">Prompts you have submitted to the library</p>
      </div>
      <div className="p-6">
        {loading && <p className="text-muted-foreground">Loading your prompts…</p>}
        {error && <p className="text-destructive">{error}</p>}
        {!loading && !error && prompts && prompts.items.length === 0 && (
          <p className="text-muted-foreground">You have not submitted any prompts yet.</p>
        )}
        {!loading && !error && prompts && prompts.items.length > 0 && (
          <>
            <p className="text-sm text-muted-foreground mb-4">{prompts.totalCount} prompt{prompts.totalCount !== 1 ? 's' : ''}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
              {prompts.items.map(p => (
                <UsePromptHandler key={p.id} promptId={p.id} promptText={p.promptText} promptTitle={p.title} onUsageTracked={() => {}}>
                  {({ onUsePrompt }) => (
                    <NewPromptCard
                      id={p.id}
                      title={p.title}
                      promptText={p.promptText}
                      category={p.category}
                      tags={p.tags}
                      submittedBy={p.submittedBy}
                      createdAt={p.createdAt}
                      updatedAt={p.updatedAt}
                      isAuthenticated={true}
                      qualityScore={p.qualityScore}
                      onUsePrompt={onUsePrompt}
                      onCardClick={() => onPromptClick(p.id)}
                    />
                  )}
                </UsePromptHandler>
              ))}
            </div>
            {prompts.totalPages > 1 && (
              <div className="flex items-center gap-2 mt-6">
                <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setCurrentPage(n => n - 1)}>Previous</Button>
                <span className="text-sm text-muted-foreground">Page {currentPage} of {prompts.totalPages}</span>
                <Button variant="outline" size="sm" disabled={currentPage >= prompts.totalPages} onClick={() => setCurrentPage(n => n + 1)}>Next</Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SavedSearchesView({ onRunSearch }: { onRunSearch: (criteria: AdvancedSearchCriteria) => void }) {
  const [searches, setSearches] = useState<SavedSearch[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSearches = () => {
    setLoading(true);
    setError(null);
    api.getSavedSearches()
      .then(data => setSearches(data))
      .catch(() => setError('Failed to load saved searches.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadSearches(); }, []);

  const handleDelete = async (id: number) => {
    try {
      await api.deleteSavedSearch(id);
      setSearches(prev => prev.filter(s => s.id !== id));
    } catch {
      setError('Failed to delete saved search.');
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-auto">
      <div className="bg-slate-700 text-white p-6">
        <div className="flex items-center space-x-2 mb-2">
          <BookOpen className="w-6 h-6" />
          <h1 className="text-2xl font-semibold">Saved Searches</h1>
        </div>
        <p className="text-slate-300">Your bookmarked search criteria</p>
      </div>
      <div className="p-6">
        {loading && <p className="text-muted-foreground">Loading…</p>}
        {error && <p className="text-destructive">{error}</p>}
        {!loading && !error && searches.length === 0 && (
          <p className="text-muted-foreground">No saved searches yet. Use the Advanced Search dialog and click "Save Search" to bookmark a search.</p>
        )}
        {!loading && !error && searches.length > 0 && (
          <div className="space-y-3">
            {searches.map(s => (
              <div key={s.id} className="flex items-center justify-between rounded-lg border border-border p-4 bg-white shadow-xs">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm truncate">{s.name}</p>
                  {s.description && <p className="text-xs text-muted-foreground mt-0.5 truncate">{s.description}</p>}
                  <p className="text-xs text-muted-foreground mt-1">Used {s.useCount} time{s.useCount !== 1 ? 's' : ''}</p>
                </div>
                <div className="flex items-center gap-2 ml-4 shrink-0">
                  <Button size="sm" onClick={() => onRunSearch(s.searchCriteria)}>Run</Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(s.id)}>Delete</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Browse Prompts View Component
function BrowsePromptsView({
  prompts,
  categories,
  availableTags,
  loading,
  error,
  searchTerm,
  setSearchTerm,
  selectedCategory,
  setSelectedCategory,
  selectedTags,
  handleTagToggle,
  clearFilters,
  currentPage,
  setCurrentPage,
  sort,
  setSort,
  onMetricsChange,
  advancedSearchCriteria,
  onAdvancedSearch,
  searchResults,
  recentSearches,
  onRecentSearchClick,
  onClearRecentSearches,
  isAuthenticated,
  userId,
  onPromptDoubleClick,
  onSaveSearch
}: BrowsePromptsViewProps) {
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-slate-600 text-white p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-2 mb-2">
              <BookOpen className="w-6 h-6" />
              <h1 className="text-2xl font-semibold">Browse Prompts</h1>
            </div>
            <p className="text-slate-300">Discover and search AI prompts from the community</p>
          </div>
          <div className="text-right">
            {loading ? (
              <div className="flex items-center justify-end gap-2">
                <Loader2 className="h-5 w-5 animate-spin text-slate-300" aria-hidden="true" />
                <span className="text-slate-300 text-sm">Loading…</span>
              </div>
            ) : (
              <>
                <div className="text-3xl font-bold">{prompts?.totalCount || 0}</div>
                <div className="text-slate-300">prompts found</div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Search Results Summary - Show if we have search results or filters active */}
      {(searchResults || searchTerm || selectedCategory || selectedTags.length > 0) && (
        <div className="border-b bg-blue-50/50 backdrop-blur">
          <div className="p-4">
            <SearchResultsSummary
              totalCount={prompts?.totalCount || 0}
              searchDurationMs={searchResults?.searchDurationMs}
              currentPage={currentPage}
              pageSize={prompts?.pageSize || 20}
              hasActiveFilters={!!(selectedCategory || selectedTags.length > 0)}
              activeFiltersCount={
                (selectedCategory ? 1 : 0) + 
                selectedTags.length
              }
              searchTerm={searchTerm}
              onClearSearch={() => setSearchTerm('')}
              onClearFilters={clearFilters}
            />
          </div>
        </div>
      )}

      <div className="flex-1 flex overflow-hidden">
        {/* Main Content */}
        <div className="flex-1 flex flex-col">
          {/* Filter Bar */}
          <div className="border-b bg-slate-50/60 backdrop-blur supports-[backdrop-filter]:bg-slate-50/40">
            <div className="p-6 pt-5">
              <FilterBar
                searchTerm={searchTerm}
                onSearchTermChange={setSearchTerm}
                selectedCategory={selectedCategory}
                onCategoryChange={setSelectedCategory}
                categories={categories}
                selectedTags={selectedTags}
                onToggleTag={handleTagToggle}
                availableTags={availableTags}
                onClearAll={clearFilters}
                advancedSearchCriteria={advancedSearchCriteria}
                onAdvancedSearch={onAdvancedSearch}
                recentSearches={recentSearches}
                onRecentSearchClick={onRecentSearchClick}
                onClearRecentSearches={onClearRecentSearches}
                enableAdvancedSearch={true}
                onSaveSearch={onSaveSearch}
                // Additional controls injected below
                extraControls={<div className="flex flex-wrap gap-3 items-center">
                  <div className="flex items-center gap-1 text-xs">
                    <label className="font-medium text-slate-600">Sort</label>
                    <select
                      value={sort}
                      onChange={e => setSort(e.target.value)}
                      className="px-2 py-1 border border-slate-300 rounded-md bg-white text-xs focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="createdAt_desc">Newest</option>
                      <option value="createdAt_asc">Oldest</option>
                      <option value="uses_desc">Most Used</option>
                      <option value="likes_desc">Most Liked</option>
                      <option value="rating_desc">Highest Rated</option>
                    </select>
                  </div>
                </div>}
              />
            </div>
          </div>

          {/* Results */}
          <div className="flex-1 overflow-auto p-6">
            {loading ? (
              <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4" aria-busy="true" aria-label="Loading prompts">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="rounded-lg border border-border bg-card p-5 space-y-3">
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-5/6" />
                    <div className="flex gap-2 pt-1">
                      <Skeleton className="h-5 w-16 rounded-full" />
                      <Skeleton className="h-5 w-16 rounded-full" />
                    </div>
                    <div className="flex justify-between pt-2">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-4 w-16" />
                    </div>
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            ) : prompts && prompts.items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <p className="text-muted-foreground text-lg mb-2">No prompts found</p>
                {currentPage > 1 ? (
                  <p className="text-sm text-muted-foreground">
                    Page {currentPage} is beyond the last page.{' '}
                    <button
                      className="underline text-blue-600 hover:text-blue-800"
                      onClick={() => setCurrentPage(1)}
                    >
                      Go to page 1
                    </button>
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">Try adjusting your search or filters.</p>
                )}
              </div>
            ) : (
              <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {prompts?.items.map((p: Prompt) => {
                  const metrics = (p.rating !== undefined || p.uses !== undefined || p.likes !== undefined || p.comments !== undefined)
                    ? {
                        rating: p.rating ?? 0,
                        ratingCount: p.ratingCount ?? 0,
                        uses: p.uses ?? 0,
                        likes: p.likes ?? 0,
                        comments: p.comments ?? 0
                      }
                    : undefined;
                  
                  return (
                    <UsePromptHandler
                      key={p.id}
                      promptId={p.id}
                      promptTitle={p.title}
                      promptText={p.promptText}
                      onUsageTracked={() => onMetricsChange(p.id, { uses: (metrics?.uses ?? 0) + 1 })}
                    >
                      {({ onUsePrompt }) => (
                        <NewPromptCard
                          id={p.id}
                          title={p.title}
                          promptText={p.promptText}
                          category={p.category}
                          tags={p.tags}
                          submittedBy={p.submittedBy}
                          createdAt={p.createdAt}
                          updatedAt={p.updatedAt}
                          isOptimistic={p.isOptimistic}
                          isAuthenticated={isAuthenticated}
                          isLikedByCurrentUser={p.isLikedByCurrentUser}
                          qualityScore={p.qualityScore}
                          metrics={metrics}
                          onMetricsChange={(newMetrics) => onMetricsChange(p.id, newMetrics)}
                          onUsePrompt={onUsePrompt}
                          onCardClick={() => onPromptDoubleClick(p.id)}
                        />
                      )}
                    </UsePromptHandler>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {prompts && prompts.totalPages > 1 && (
              <div className="mt-8 flex justify-center">
                <div className="flex gap-2">
                  <Button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={!prompts.hasPreviousPage}
                    variant="outline"
                  >
                    Previous
                  </Button>
                  <span className="px-4 py-2 text-gray-600">
                    {Math.min(currentPage, prompts.totalPages)} of {prompts.totalPages}
                  </span>
                  <Button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={!prompts.hasNextPage}
                    variant="outline"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Search Facets Sidebar - Show when we have faceted search results */}
        {searchResults?.facets && (
          <div className="w-80 border-l bg-slate-50/30 backdrop-blur">
            <div className="p-6">
              <SearchFacetsPanel
                facets={searchResults.facets}
                onCategoryClick={(category) => {
                  const categoryObj = categories.find(c => c.name === category);
                  if (categoryObj) {
                    setSelectedCategory(selectedCategory === categoryObj.id ? undefined : categoryObj.id);
                  }
                }}
                onTagClick={(tag) => {
                  handleTagToggle(tag);
                }}
                onSubmitterClick={(submitter) => {
                  // TODO: Add submitter filtering
                  console.log('Filter by submitter:', submitter);
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Submit Prompt View Component
function SubmitPromptView({ categories, availableTags, currentUserDisplayName, onSuccess, addOptimisticPrompt, finalizeOptimisticPrompt, revertOptimisticPrompt }: Readonly<SubmitPromptViewProps>) {
  const { instance: msalInstance } = useSafeMsal();
  const PROMPT_TEXT_MAX_LENGTH = 50000;
  const TAG_MAX_LENGTH = 50;
  const TAG_PATTERN = /^[a-zA-Z0-9\-_]+$/;

  const normalizeTag = (value: string) => value.trim().toLowerCase().replace(/\s+/g, '-');

  const schema = z.object({
    title: z.string().trim().min(1, 'Missing Title').max(200, 'Title exceeds 200 character limit'),
    promptText: z.string().trim().min(1, 'Prompt text must be at least 1 character').max(PROMPT_TEXT_MAX_LENGTH, `Prompt text exceeds ${PROMPT_TEXT_MAX_LENGTH.toLocaleString()} character limit`),
    // description optional: allow empty string or up to 500 chars. Use single string with refine for simplicity.
    description: z
      .string()
      .max(500, 'Description exceeds 500 character limit')
      .optional()
      .transform((val: string | undefined) => (val === undefined ? '' : val))
      .refine((val: string) => val.length === 0 || val.length <= 500, 'Description exceeds 500 character limit'),
    submittedBy: z.string().min(2).max(40),
    categoryId: z.number().int().positive('Missing Category'),
    tags: z.array(
      z
        .string()
        .min(1)
        .max(TAG_MAX_LENGTH, `Each tag must be ${TAG_MAX_LENGTH} characters or fewer`)
        .regex(TAG_PATTERN, 'Tags can only contain letters, numbers, hyphens, and underscores')
    ).max(10, 'Too many tags (max 10)')
  });

  type PromptFormValues = z.infer<typeof schema>;

  const form = useZodForm<PromptFormValues>({
    schema,
    defaultValues: {
      title: '',
      promptText: '',
      description: '',
      submittedBy: currentUserDisplayName,
      categoryId: categories[0]?.id || 0,
      tags: []
    }
  });

  const { handleSubmit, setValue, watch, formState } = form;
  const tags = watch('tags');
  const watchedTitle = watch('title');
  const watchedPromptText = watch('promptText');
  const watchedCategoryId = watch('categoryId');
  const watchedDescription = watch('description');
  const [tagInput, setTagInput] = useState('');
  const [focusedSuggestion, setFocusedSuggestion] = useState<number>(-1);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState<string>('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [attachFileError, setAttachFileError] = useState<string>('');
  const attachInputRef = React.useRef<HTMLInputElement>(null);

  // FR-7.2 quality analysis state
  const [qualityAnalysis, setQualityAnalysis] = useState<PromptQualityAnalysis | null>(null);
  const [pendingValues, setPendingValues] = useState<PromptFormValues | null>(null);
  const [analysisFailed, setAnalysisFailed] = useState(false);
  const autoApproveThreshold = useAutoApproveThreshold();
  // Guards against an orphaned save when the user navigates away mid-analysis
  const isMountedRef = React.useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const BLOCKED_EXTENSIONS = ['.exe', '.bat', '.sh', '.ps1', '.cmd', '.msi', '.dmg', '.app'];
  const MAX_ATTACH_BYTES = 10 * 1024 * 1024;

  const validateAttachFile = (file: File): string | null => {
    const lower = file.name.toLowerCase();
    if (BLOCKED_EXTENSIONS.some(ext => lower.endsWith(ext)))
      return `Executable files are not allowed: "${file.name}"`;
    if (file.size > MAX_ATTACH_BYTES)
      return `"${file.name}" exceeds the 10 MB size limit`;
    if (file.size === 0)
      return `"${file.name}" is empty`;
    return null;
  };

  const handleAttachFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const err = validateAttachFile(file);
    if (err) { setAttachFileError(err); return; }
    setAttachFileError('');
    setPendingFiles(prev => [...prev, file]);
  };

  const removePendingFile = (idx: number) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== idx));
  };

  // Compute unmet requirements from watched field values so the panel updates on every keystroke
  const unmetRequirements = React.useMemo(() => {
    const issues: string[] = [];
    const title = (watchedTitle ?? '').trim();
    const promptText = (watchedPromptText ?? '').trim();
    const description = watchedDescription ?? '';

    if (!title) issues.push('Missing Title');
    else if (title.length > 200) issues.push('Title exceeds 200 character limit');

    if (!promptText) issues.push('Prompt text must be at least 1 character');
    else if (promptText.length > PROMPT_TEXT_MAX_LENGTH) issues.push(`Prompt text exceeds ${PROMPT_TEXT_MAX_LENGTH.toLocaleString()} character limit`);

    if (!watchedCategoryId || watchedCategoryId <= 0) issues.push('Missing Category');

    if (description.length > 500) issues.push('Description exceeds 500 character limit');

    if ((tags ?? []).length > 10) issues.push('Too many tags (max 10)');

    return issues;
  }, [watchedTitle, watchedPromptText, watchedCategoryId, watchedDescription, tags]);

  const isFormValid = unmetRequirements.length === 0;

  const normalizedExisting = tags.map((t: string) => t.toLowerCase());
  const suggestions = React.useMemo(() => {
    const input = tagInput.trim().toLowerCase();
    if (!input) return [] as Tag[];
    return availableTags
      .filter(t => t.name.toLowerCase().includes(input))
      .filter(t => !normalizedExisting.includes(t.name.toLowerCase()))
      .slice(0, 8);
  }, [tagInput, availableTags, normalizedExisting]);

  const commitTag = (raw?: string, fromSuggestion?: boolean) => {
    const base = normalizeTag(raw ?? tagInput);
    if (!base) return;
    if (tags.length >= 10) {
      toast.error('Too many tags', { description: 'Maximum 10 tags allowed' });
      return;
    }
    if (base.length > TAG_MAX_LENGTH) {
      toast.error('Tag is too long', { description: `Each tag must be ${TAG_MAX_LENGTH} characters or fewer` });
      return;
    }
    if (!TAG_PATTERN.test(base)) {
      toast.error('Invalid tag', { description: 'Use only letters, numbers, hyphens, and underscores' });
      return;
    }
    if (normalizedExisting.includes(base)) {
      toast.info('Tag already added', { description: base });
      return;
    }
    setValue('tags', [...tags, base]);
    setTagInput('');
    setFocusedSuggestion(-1);
    if (!fromSuggestion) setShowSuggestions(false);
  };

  const addTag = () => commitTag();

  const removeTag = (tagToRemove: string) => {
    setValue('tags', tags.filter(t => t !== tagToRemove));
  };

  const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (showSuggestions && focusedSuggestion >= 0 && suggestions[focusedSuggestion]) {
        commitTag(suggestions[focusedSuggestion].name, true);
      } else {
        commitTag();
      }
    } else if (e.key === 'ArrowDown') {
      if (suggestions.length) {
        e.preventDefault();
        setShowSuggestions(true);
        setFocusedSuggestion(prev => (prev + 1) % suggestions.length);
      }
    } else if (e.key === 'ArrowUp') {
      if (suggestions.length) {
        e.preventDefault();
        setShowSuggestions(true);
        setFocusedSuggestion(prev => (prev - 1 + suggestions.length) % suggestions.length);
      }
    } else if (e.key === 'Escape') {
      if (showSuggestions) {
        e.preventDefault();
        setShowSuggestions(false);
        setFocusedSuggestion(-1);
      }
    }
  };

  const savePrompt = async (values: PromptFormValues, analysisId: string, qualityScore: number) => {
    const loadingToastId = toast.loading('Saving prompt...');
    // Insert optimistic prompt
    const tempId = addOptimisticPrompt(values);
    try {
      const result = await api.createPrompt({ ...values, analysisId });
      console.log('Prompt created successfully:', result);
      // Upload any pending attachments
      if (pendingFiles.length > 0) {
        for (const file of pendingFiles) {
          try {
            await api.uploadAttachment((result as any).id, file);
          } catch (uploadErr: any) {
            toast.error(`Attachment "${file.name}" failed to upload: ${uploadErr?.message || 'unknown error'}`);
          }
        }
        setPendingFiles([]);
      }
      // finalize with real data
      finalizeOptimisticPrompt(tempId, result as unknown as Prompt);
      onSuccess();
      form.reset();
      setSubmitStatus('success');
      setSubmitMessage(`Saved — Quality Score: ${qualityScore}/10`);
      toast.success(`Saved — Quality Score: ${qualityScore}/10`, { id: loadingToastId });
      requestAnimationFrame(() => {
        const heading = document.getElementById('submit-prompt-heading');
        heading?.focus();
      });
    } catch (error: any) {
      console.error('Failed to create prompt:', error);
      revertOptimisticPrompt(tempId);
      setSubmitStatus('error');
      setSubmitMessage(`Failed to create prompt: ${error?.message || error}`);
      toast.error('Failed to create prompt', {
        id: loadingToastId,
        description: error?.message || 'An unknown error occurred'
      });
    }
  };

  // FR-7.2: every submit runs the quality analysis first. Score >= threshold saves
  // immediately with the returned analysisId; below threshold opens the review modal;
  // analysis failure blocks saving (fail closed — no bypass, no save-as-draft).
  const onSubmit = async (values: PromptFormValues) => {
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setAnalysisFailed(false);
    const analyzingToastId = toast.loading('Analyzing prompt quality...');
    let analysis: PromptQualityAnalysis | null = null;
    try {
      analysis = await api.analyzePrompt({
        title: values.title,
        promptText: values.promptText,
        categoryId: values.categoryId,
        tags: values.tags
      });
      toast.dismiss(analyzingToastId);
    } catch (error: any) {
      console.error('Quality analysis failed:', error);
      if (error instanceof AuthSessionExpiredError) {
        toast.error('Your session has expired', {
          id: analyzingToastId,
          description: 'Please sign in again to continue.',
          action: {
            label: 'Sign In Again',
            onClick: () => msalInstance?.loginRedirect(loginRequest),
          },
        });
      } else {
        toast.error('Quality analysis unavailable. Please try again.', {
          id: analyzingToastId,
          description: error?.message || undefined
        });
      }
      if (isMountedRef.current) {
        setAnalysisFailed(true);
        setIsSubmitting(false);
      }
      return;
    }
    if (!analysis || !isMountedRef.current) return; // user navigated away mid-analysis — no orphaned save
    try {
      if (analysis.qualityScore >= autoApproveThreshold) {
        await savePrompt(values, analysis.analysisId, analysis.qualityScore);
      } else {
        setPendingValues(values);
        setQualityAnalysis(analysis);
      }
    } finally {
      if (isMountedRef.current) setIsSubmitting(false);
    }
  };

  const handleAcceptChanges = async () => {
    if (!pendingValues || !qualityAnalysis?.suggestedRewrite) return;
    const rewrittenValues = { ...pendingValues, promptText: qualityAnalysis.suggestedRewrite };
    setIsSubmitting(true);
    try {
      // The save endpoint verifies the analysisId's content hash against the submitted
      // content, so the rewrite needs its own analysis before it can be saved.
      const rewriteAnalysis = await api.analyzePrompt({
        title: rewrittenValues.title,
        promptText: rewrittenValues.promptText,
        categoryId: rewrittenValues.categoryId,
        tags: rewrittenValues.tags
      });
      if (!isMountedRef.current) return;
      // Push the accepted rewrite into the form before saving so a failed save
      // doesn't silently discard it (savePrompt reports its own errors).
      setValue('promptText', rewrittenValues.promptText);
      setQualityAnalysis(null);
      setPendingValues(null);
      await savePrompt(rewrittenValues, rewriteAnalysis.analysisId, rewriteAnalysis.qualityScore);
    } catch (error: any) {
      console.error('Quality analysis failed for suggested rewrite:', error);
      // Keep the modal open so the user can retry Accept or fall back to Edit & Resubmit
      if (error instanceof AuthSessionExpiredError) {
        toast.error('Your session has expired', {
          description: 'Please sign in again to continue.',
          action: {
            label: 'Sign In Again',
            onClick: () => msalInstance?.loginRedirect(loginRequest),
          },
        });
      } else {
        toast.error('Quality analysis unavailable. Please try again.', {
          description: error?.message || undefined
        });
      }
    } finally {
      if (isMountedRef.current) setIsSubmitting(false);
    }
  };

  const handleEditResubmit = () => {
    // Original content stays untouched in the form; next submit re-analyzes
    setQualityAnalysis(null);
    setPendingValues(null);
  };

  return (
    <>
      {/* Header */}
      <div className="bg-slate-600 text-white p-6" role="region" aria-labelledby="submit-prompt-heading">
        <div className="flex items-center space-x-2 mb-2">
          <Plus className="w-6 h-6" aria-hidden="true" />
          <h1 id="submit-prompt-heading" tabIndex={-1} className="text-2xl font-semibold focus:outline-none">Submit New Prompt</h1>
        </div>
        <p className="text-slate-300">Share your AI prompt with the community</p>
      </div>

      {/* Form */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-2xl">
          <Card className="p-6">
            <div className="mb-6" role="heading" aria-level={2}>
              <div className="flex items-center space-x-2 text-blue-600 mb-4">
                <div className="w-2 h-2 bg-blue-600 rounded-full" aria-hidden="true"></div>
                <span className="font-medium">Prompt Details</span>
              </div>
            </div>

            <FormProvider {...form}>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" aria-describedby="required-hint" noValidate>
              <p id="required-hint" className="sr-only">Fields marked with * are required.</p>
              {/* Live region for submission status */}
              <div aria-live="polite" role="status" className="sr-only">
                {submitStatus === 'success' && submitMessage}
                {submitStatus === 'error' && submitMessage}
              </div>
              {/* Toasts handle visual success/error; keep messages in live region only */}
              <FormField
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor={field.name}>Title <span className="text-red-500" aria-hidden="true">*</span></FormLabel>
                    <Input placeholder="Enter a descriptive title for your prompt" {...field} />
                    <FormMessage aria-live="polite">{formState.errors.title?.message}</FormMessage>
                  </FormItem>
                )}
              />
              <FormField
                name="promptText"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor={field.name}>Prompt Text <span className="text-red-500" aria-hidden="true">*</span></FormLabel>
                    <Textarea
                      rows={8}
                      placeholder="Enter your AI prompt here..."
                      maxLength={PROMPT_TEXT_MAX_LENGTH}
                      {...field}
                    />
                    <FormDescription>
                      {(field.value?.length || 0).toLocaleString()}/{PROMPT_TEXT_MAX_LENGTH.toLocaleString()} characters
                    </FormDescription>
                    <FormMessage aria-live="polite">{formState.errors.promptText?.message}</FormMessage>
                  </FormItem>
                )}
              />
              <FormField
                name="categoryId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor={field.name}>Category <span className="text-red-500" aria-hidden="true">*</span></FormLabel>
                    <select
                      value={field.value}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      aria-invalid={!!formState.errors.categoryId}
                      aria-describedby={formState.errors.categoryId ? 'categoryId-error' : undefined}
                    >
                      <option value="">Select a category</option>
                      {categories.map((cat: Category) => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                    <FormMessage id="categoryId-error" aria-live="polite">{formState.errors.categoryId?.message}</FormMessage>
                  </FormItem>
                )}
              />
              {/* Tags */}
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel htmlFor="tag-input">Tags</FormLabel>
                  <span className={cn('text-xs', tags.length >= 10 ? 'text-red-500 font-medium' : 'text-gray-500')}>{tags.length}/10</span>
                </div>
                <div className="flex space-x-2 mb-2" role="group" aria-labelledby="tag-input">
                  <Input
                    id="tag-input"
                    value={tagInput}
                    onChange={(e) => {
                      setTagInput(e.target.value);
                      setShowSuggestions(true);
                      setFocusedSuggestion(-1);
                    }}
                    onKeyDown={handleTagInputKeyDown}
                    placeholder={tags.length >= 10 ? 'Maximum 10 tags reached' : 'Add tags (press Enter or click + to add)'}
                    className="flex-1"
                    disabled={tags.length >= 10}
                    aria-autocomplete="list"
                    aria-expanded={showSuggestions && suggestions.length > 0}
                    aria-activedescendant={focusedSuggestion >= 0 && suggestions[focusedSuggestion] ? `tag-suggestion-${suggestions[focusedSuggestion].id}` : undefined}
                    aria-owns="tag-suggestion-list"
                  />
                  <Button type="button" onClick={addTag} variant="secondary" aria-label="Add tag" disabled={tags.length >= 10}>+</Button>
                </div>
                {showSuggestions && suggestions.length > 0 && (
                  <ul
                    id="tag-suggestion-list"
                    role="listbox"
                    className="mb-3 max-h-48 overflow-auto rounded-md border bg-white dark:bg-slate-800 shadow-sm divide-y divide-slate-100 dark:divide-slate-700"
                  >
                    {suggestions.map((s, idx) => {
                      const isActive = idx === focusedSuggestion;
                      return (
                        <li
                          id={`tag-suggestion-${s.id}`}
                          key={s.id}
                          role="option"
                          aria-selected={isActive}
                          className={cn(
                            'px-3 py-2 text-sm cursor-pointer flex items-center justify-between',
                            isActive ? 'bg-blue-600 text-white' : 'hover:bg-slate-100 dark:hover:bg-slate-700'
                          )}
                          onMouseEnter={() => setFocusedSuggestion(idx)}
                          onMouseDown={(e) => { e.preventDefault(); commitTag(s.name, true); }}
                        >
                          <span>{s.name}</span>
                          <span className="text-xs text-slate-400 dark:text-slate-300">Add</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
                <p className="text-sm text-gray-500 mb-3">Tags help others find your prompt. Add relevant keywords.</p>
                {tags.length > 0 && (
                  <ul className="flex flex-wrap gap-2" aria-label="Current tags">
                    {tags.map((tag: string) => (
                      <li key={tag} className="list-none">
                        <Badge variant="secondary" className="flex items-center gap-1">
                          <span>{tag}</span>
                          <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            className="text-gray-600 hover:text-gray-900"
                            aria-label={`Remove tag ${tag}`}
                          >
                            <X className="w-3 h-3" aria-hidden="true" />
                          </button>
                        </Badge>
                      </li>
                    ))}
                  </ul>
                )}
                <FormMessage aria-live="polite">{formState.errors.tags?.message as string}</FormMessage>
              </FormItem>
              <FormField
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor={field.name}>Description (optional)</FormLabel>
                    <Textarea rows={4} placeholder="Optional description or usage notes" {...field} />
                    <FormMessage aria-live="polite">{formState.errors.description?.message}</FormMessage>
                  </FormItem>
                )}
              />
              {/* File Attachments */}
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel>Attachments (optional)</FormLabel>
                  <span className="text-xs text-gray-500">Max 10 MB per file</span>
                </div>
                <input
                  ref={attachInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleAttachFileChange}
                  aria-label="Attach a file"
                />
                <button
                  type="button"
                  onClick={() => { setAttachFileError(''); attachInputRef.current?.click(); }}
                  className="flex items-center gap-2 px-3 py-2 text-sm border border-dashed border-gray-300 rounded-md text-gray-600 hover:border-blue-400 hover:text-blue-600 w-full justify-center"
                >
                  <Paperclip className="w-4 h-4" aria-hidden="true" />
                  Attach a file
                </button>
                {attachFileError && (
                  <p className="text-xs text-red-600 mt-1" role="alert">{attachFileError}</p>
                )}
                {pendingFiles.length > 0 && (
                  <ul className="mt-2 space-y-1" aria-label="Files to attach">
                    {pendingFiles.map((f, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm bg-gray-50 rounded px-3 py-2">
                        <Paperclip className="w-4 h-4 text-gray-500 flex-shrink-0" aria-hidden="true" />
                        <span className="flex-1 truncate" title={f.name}>{f.name}</span>
                        <span className="text-xs text-gray-400">{(f.size / 1024).toFixed(0)} KB</span>
                        <button
                          type="button"
                          onClick={() => removePendingFile(idx)}
                          className="text-red-500 hover:text-red-700"
                          aria-label={`Remove ${f.name}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </FormItem>
              {/* Submitted By (read-only visual) */}
              <FormItem>
                <FormLabel>Submitted By</FormLabel>
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 bg-slate-500 rounded-full flex items-center justify-center text-white text-sm">
                    {currentUserDisplayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                  </div>
                  <span className="font-medium">{currentUserDisplayName}</span>
                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">Current User</span>
                </div>
                <FormDescription>Submitting as your signed-in account.</FormDescription>
              </FormItem>
              {/* Validation summary — shown whenever there are unmet requirements */}
              {unmetRequirements.length > 0 && (
                <div
                  role="alert"
                  aria-label="Validation requirements"
                  className="rounded-md border border-amber-200 bg-amber-50 p-4"
                >
                  <p className="text-sm font-medium text-amber-800 mb-2">
                    To enable Submit, please address the following:
                  </p>
                  <ul className="space-y-1" aria-label="Unmet requirements">
                    {unmetRequirements.map((msg) => (
                      <li key={msg} className="flex items-center gap-2 text-sm text-amber-700">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" aria-hidden="true" />
                        {msg}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {/* FR-7.2 failure path: analysis is required before save — no bypass */}
              {analysisFailed && (
                <div
                  role="alert"
                  className="flex items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 p-3"
                >
                  <p className="text-sm text-red-700">Quality analysis unavailable. Please try again.</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleSubmit(onSubmit)}
                    disabled={isSubmitting}
                  >
                    Retry
                  </Button>
                </div>
              )}
              <div className="pt-2">
                <Button
                  type="submit"
                  disabled={isSubmitting || !isFormValid || analysisFailed}
                  className="w-full relative"
                  variant="destructive"
                  aria-disabled={isSubmitting || !isFormValid || analysisFailed}
                >
                  {isSubmitting && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                  )}
                  {isSubmitting ? 'Submitting...' : '🚀 Submit Prompt'}
                </Button>
              </div>
            </form>
            </FormProvider>
          </Card>
        </div>
      </div>

      {/* FR-7.2 comparison modal — shown when analysis scores below the threshold */}
      <QualityReviewModal
        open={!!qualityAnalysis}
        analysis={qualityAnalysis}
        originalText={pendingValues?.promptText ?? ''}
        isSaving={isSubmitting}
        onAcceptChanges={handleAcceptChanges}
        onEditResubmit={handleEditResubmit}
      />
    </>
  );
}
